import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Textarea } from "@delibrandev/utils";
import { IoMdClose } from "react-icons/io";
import { CartActions } from "../../../components/cartActions";

import { Combinations, getDefaultVariantId } from "./combinations";
import { Attributes } from "./Attributes";
import { formatMoney, priceWithTax } from "../../../utils/pricing";
import "./productDetails.css";

// Imágenes alérgenos
import celery from "../../../assets/allergens/celery.png";
import crustaceans from "../../../assets/allergens/crustaceans.png";
import eggs from "../../../assets/allergens/eggs.png";
import fish from "../../../assets/allergens/fish.png";
import gluten from "../../../assets/allergens/gluten.png";
import lactose from "../../../assets/allergens/lactose.png";
import lupin from "../../../assets/allergens/lupin.png";
import molluscs from "../../../assets/allergens/molluscs.png";
import mustard from "../../../assets/allergens/mustard.png";
import nuts from "../../../assets/allergens/nuts.png";
import peanuts from "../../../assets/allergens/peanuts.png";
import sesame from "../../../assets/allergens/sesame.png";
import soy from "../../../assets/allergens/soy.png";
import sulfites from "../../../assets/allergens/sulfites.png";

const toNumber = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (v == null) return 0;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

// Alérgenos
const allergenImages = {
  celery,
  crustaceans,
  eggs,
  fish,
  gluten,
  lactose,
  lupin,
  molluscs,
  mustard,
  nuts,
  peanuts,
  sesame,
  soy,
  sulfites,
};

const allergenNames = {
  celery: "Apio",
  crustaceans: "Crustáceos",
  eggs: "Huevos",
  fish: "Pescado",
  gluten: "Gluten",
  lactose: "Lactosa",
  lupin: "Altramuces",
  molluscs: "Moluscos",
  mustard: "Mostaza",
  nuts: "Frutos secos",
  peanuts: "Cacahuetes",
  sesame: "Sésamo",
  soy: "Soja",
  sulfites: "Sulfitos",
};


// Accept the API's JSON column as an object or serialized object, including its wrapper.
// Never interpret string character indexes or internal metadata as allergen names.
export const normalizeAllergens = (input, knownKeys) => {
  let value = input;
  try {
    for (let depth = 0; depth < 4; depth++) {
      if (typeof value === "string") { value = JSON.parse(value); continue; }
      if (value && typeof value === "object" && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, "allergens")) {
        value = value.allergens; continue;
      }
      break;
    }
  } catch { return { entries: [], invalid: true }; }
  if (value == null) return { entries: [], invalid: false };
  if (typeof value !== "object" || Array.isArray(value)) return { entries: [], invalid: true };
  const entries = [];
  let invalid = false;
  for (const [key, present] of Object.entries(value)) {
    if (key === "none") continue; // Existing internal marker; no absence claim is inferred.
    const positive = [true, 1, "true", "1"].includes(present);
    const negative = [false, 0, "false", "0", null].includes(present);
    if (!knownKeys.includes(key)) { invalid = true; continue; }
    if (positive) entries.push(key);
    else if (!negative) invalid = true;
  }
  return { entries, invalid };
};

export const ProductDetails = ({ productDetailsModal, setProductDetailsModal }) => {
  const productData = productDetailsModal;
  const dialogRef = useRef(null);
  const [additionState, setAdditionState] = useState({locked:false,busy:false});
  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.querySelector("button")?.focus();
    return () => {
      if (previous?.isConnected && previous !== document.body && previous !== document.documentElement) previous.focus();
      else document.querySelector('[aria-label="Buscar producto"]')?.focus();
    };
  }, []);
  const handleDialogKey = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!additionState.busy) setProductDetailsModal(null);
    }
    if (event.key !== "Tab") return;
    const controls = [
      ...dialogRef.current.querySelectorAll(
        'button:not(:disabled),input:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
      ),
    ].filter((element) => element.offsetParent !== null);
    const first = controls[0],
      last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  const urlImgDemo = "https://www.mollinn.com/img/default-product-img.jpg";

  const [itemIds, setItemIds] = useState([]);
  const [variantId, setVariantId] = useState("");
  const [note, setNote] = useState("");
  const [quantity, updateQuantity] = useState(0);
  const [requirements, updateRequirements] = useState([{ id: 0, error: "" }]);

  const productIdentity = productData?.productId ?? productData?.id ?? null;

  // Solo reiniciamos la configuración cuando realmente se abre otro producto.
  // Las actualizaciones de Redux en background no deben borrar atributos,
  // variante, cantidad o notas que el usuario esté editando.
  useEffect(() => {
    setItemIds([]);
    setVariantId(getDefaultVariantId(productData));
    setNote("");
    updateQuantity(0);
  }, [productIdentity, productData]);

  // Imágenes
  const images = useMemo(() => {
    const a = Array.isArray(productData?.images) ? productData.images : [];
    const b = Array.isArray(productData?.productImages) ? productData.productImages : [];
    const merged = a.length ? a : b;
    return merged.length ? merged : [urlImgDemo];
  }, [productData]);

  const { entries: allergenEntries, invalid: invalidAllergens } = useMemo(
    () => normalizeAllergens(productData?.allergens, Object.keys(allergenNames)),
    [productData],
  );

  // Selección opciones
  const handleOption = useCallback((item) => {
    setItemIds((prev) => {
      if (prev.includes(item.itemId)) return prev.filter((id) => id !== item.itemId);
      return [...prev, item.itemId];
    });
  }, []);

  // Upsert requirements (estable con useCallback)
  const handleRequeriments = useCallback(({ id, error }) => {
    updateRequirements((prev) => {
      const index = prev.findIndex((item) => item.id === id);

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = { id, error };
        return updated;
      }

      return [...prev, { id, error }];
    });
  }, []);

  // Precio variaciones
  const variationPrice = useMemo(() => {
    const itemsPrice = (productData?.itemGroups || []).reduce((total, group) => {
      const groupTotal = (group.items || [])
        .filter((item) => itemIds.includes(item.itemId))
        .reduce((sum, item) => sum + toNumber(item.price), 0);

      return total + groupTotal;
    }, 0);

    const variant = (productData?.variants || []).find((f) => f.variantId === variantId);
    const variantPrice = variant ? toNumber(variant.price) : 0;

    return itemsPrice + variantPrice;
  }, [productData, itemIds, variantId]);

  const totalPrice = useMemo(() => {
    const basePrice = toNumber(productData?.price);
    return priceWithTax(basePrice + variationPrice, productData);
  }, [productData, variationPrice]);


  return (
    <div
      ref={dialogRef}
      onKeyDownCapture={handleDialogKey}
      className="productDetails"
      role="dialog"
      aria-modal="true"
      aria-busy={additionState.busy}
      aria-label={`Configurar ${productData?.name || "producto"}`}
    >
      <div className="productDetails-header">
        <button
          type="button"
          className="tpvClose"
          aria-label="Cerrar producto"
          disabled={additionState.busy}
          onClick={() => setProductDetailsModal(null)}
        >
          <IoMdClose aria-hidden="true" />
        </button>
      </div>

      <div className="productDetails-scroll-content" inert={additionState.locked ? "" : undefined}>
        {/* HEADER */}
        <div className="productDetails-data">
          <div className="productDetails-img">
            <img
              src={images[0]}
              alt={productData?.shortName ?? productData?.name ?? "product"}
              className="brandDashboard"
            />
          </div>

          <div className="productDetails-info">
            <div>
              <b>{productData?.shortName ?? productData?.name}</b>
            </div>
            <div>{productData?.description}</div>

            {images.length > 1 && (
              <div className="mt-2 flex-start gap-2" style={{ padding: 0 }}>
                {images.map((image, id) => (
                  <div
                    key={id}
                    className="details-image"
                    style={{
                      backgroundImage: image ? `url('${image}')` : "none",
                      width: "50px",
                      height: "50px",
                      borderRadius: "10px",
                    }}
                  />
                ))}
              </div>
            )}

            {(allergenEntries.length > 0 || invalidAllergens) && (
              <section className="alergenosBlock" aria-label="Alérgenos" style={{ padding: "10px 0px" }}>
                {invalidAllergens && <p role="alert">No se puede confirmar la información de alérgenos. Consulta al establecimiento.</p>}
                <div className="flex-start" style={{ gap: "10px", flexWrap: "wrap" }}>
                  {allergenEntries.map((key) => (
                    <div key={key} className="details-allergen-list-item">
                      <div className="allergen-item">
                        <div><img src={allergenImages[key]} alt="" className="allergen-icon" /></div>
                        <div className="allergen-name">{allergenNames[key]}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        <div className="configuration-product">
          {/* VARIANTES */}
          <div className="productDetails-block">
            <Combinations productData={productData} variantId={variantId} setVariantId={setVariantId} />
          </div>

          {/* ATRIBUTOS */}
          {productData?.itemGroups?.map(
            (group, index) =>
              group.isActive && (
                <div key={group.itemGroupId ?? index} className="productDetails-block">
                  <Attributes
                    group={group}
                    taxSource={productData}
                    index={index}
                    handleOption={handleOption}
                    itemIds={itemIds}
                    updateRequirements={handleRequeriments}
                  />
                </div>
              ),
          )}

          {/* NOTAS */}
          <div className="productDetails-block">
            <h5 style={{ marginBottom: "-13px" }}>
              <b>Notas</b>
            </h5>
            <Textarea id="product-details-notes" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="productItemInfo productItemInfo-fixed">
        <div>{formatMoney(totalPrice)}</div>
        {requirements.filter((requirement) => requirement.error !== "").length === 0 ? (
          <CartActions
            addLabel="Añadir al pedido"
            data={{ ...productData, quantity, variantId, itemIds, note }}
            callback={() => setProductDetailsModal(null)}
            confirmAddition
            onConfirmationState={setAdditionState}
            showRemove={false}
            showCounter={false}
          />
        ) : (
          <div className="productItemInfo-error">
            {requirements.filter((requirement) => requirement.error !== "")[0].error}
          </div>
        )}
      </div>
    </div>
  );
};
